import { Card, EmptyState, PageHeader, QueryError, Space } from '@bookora/ui';
import { useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/auth/token-store';
import { LinkButton } from '@/components/link-button';
import { TableSkeleton } from '@/components/form-bits';
import { useDocumentTitle } from '@/hooks/use-document-title';

import { useStaffList } from './hooks';
import { TimeOffEditor } from './time-off-editor';
import { BranchWorkingHoursEditor } from './branch-working-hours';

/**
 * One colleague's week, on a page of its own.
 *
 * This is the screen `/staff/:staffId/working-hours` renders — the link the
 * calendar follows when a shift looks wrong and the link the settings section's
 * roster hands out. It is the same two editors the staff drawer shows, mounted
 * without the drawer, so a deep link lands somewhere that makes sense on its
 * own rather than opening a modal over a list the user did not ask for.
 */
export default function StaffSchedulePage() {
  const { staffId } = useParams<{ staffId: string }>();
  // `?branch=<id>` opens that branch's roster (the branch card's link).
  const [params] = useSearchParams();
  const { activeClinic } = useAuth();
  const staff = useStaffList();

  const member = (staff.data ?? []).find((row) => row.id === staffId);
  const name = member?.displayName ?? member?.fullName ?? 'کارمند';

  useDocumentTitle(`برنامهٔ کاری ${name}`, activeClinic?.clinicName);

  if (staff.isLoading) {
    return (
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Card title="شیفت‌های هفتگی">
          <TableSkeleton rows={4} label="در حال بارگذاری برنامهٔ کاری…" />
        </Card>
        <Card title="مرخصی‌ها">
          <TableSkeleton rows={2} />
        </Card>
      </Space>
    );
  }
  if (staff.error) return <QueryError error={staff.error} onRetry={() => void staff.refetch()} />;

  // Only a roster that WAS read can say somebody is not on it; a caller
  // without `staff.view` has no roster and still gets the editors.
  if (staff.data && staffId && !member) {
    return (
      <EmptyState
        title="این کارمند پیدا نشد"
        description="ممکن است حسابش حذف شده باشه یا لینک اشتباه باشه. از فهرست کارکنان دوباره انتخابش کنید."
        action={<LinkButton to="/staff">فهرست کارکنان</LinkButton>}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={`برنامهٔ کاری ${name}`}
        description="شیفت‌های هفتگی و مرخصی‌ها. نوبت‌ها بر اساس همین برنامه داده می‌شن."
        // The breadcrumb (drawn once, by the shell) is the way back; a
        // «بازگشت» button beside it was a third route to the same list.
        crumbs={[{ label: 'کارکنان و نقش‌ها', to: '/staff' }, { label: 'برنامهٔ کاری' }]}
      />

      {staffId ? (
        // Each editor in a titled card, like every other screen in the panel:
        // two bare tables on one page gave no clue which was shifts and which
        // was leave.
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Card title="شیفت‌های هفتگی">
            <BranchWorkingHoursEditor
              staffId={staffId}
              member={member ?? null}
              initialBranchId={params.get('branch')}
            />
          </Card>
          <Card title="مرخصی‌ها">
            <TimeOffEditor staffId={staffId} staffName={name} />
          </Card>
        </Space>
      ) : null}
    </>
  );
}
